/**
 * Targeted Bond Adjuster - Handles targeted bond adjustments during candidate processing
 *
 * This is NOT an optimization - it's a candidate-level adjustment that happens during
 * shape search (first-fit and best-fit phases) for any candidate with targeted bonds.
 *
 * Process flow:
 * 1. Analyze shortfalls for targeted bonds
 * 2. For each targeted bond (in issue order):
 *    - Find capacity and add principal
 *    - Reprocess via CandidateProcessor
 *    - Repeat if needed (max 2 iterations per bond)
 * 3. Return adjusted candidate
 *
 * This is called internally by CandidateProcessor when targeted bonds are detected.
 */

import { TARGETED_BOND_PROCESSING } from '~/config/financial-constants';
import { logger } from '~/services/logging';
import { type BondSeries, type Candidate, type Configuration, TargetType } from '~/types';
// Note: We don't import CandidateProcessor here to avoid circular dependency
// This adjuster is called BY CandidateProcessor, not the other way around
import {
  calculateInterestMultiplier,
  calculateMaxPrincipalWithInterest,
  calculateOptimizationScore,
} from '../../calculators/analysis/capacity-analysis-calculator';
import { roundPrincipalNearest } from '../../calculators/core/financial-math/rounding';
import { calculateActualAdjustment } from '../../calculators/metrics/optimization-metrics-calculator';
import {
  calculateDrfAvailableCapacity,
  calculateTargetShortfall,
  isTargetMetWithinTolerance,
} from '../../calculators/validation/target-validation-calculator';
import type { ProcessingResult } from '../../types';
import { PrincipalBasedRecalculationEngine } from '../cashflow-engine/principal-recalculator';
import {
  type FinalizationResult,
  ProcessingFinalizer,
} from '../cashflow-engine/processing-finalizer';

export interface TargetedProcessingResult extends ProcessingResult {
  targetedAdjustments?: {
    bondSeriesId: string;
    iterations: number;
    totalAdded: number;
    targetMet: boolean;
  }[];
}

export class TargetedBondAdjuster {
  /**
   * Adjusts a candidate with targeted bonds to meet their targets
   * Called internally by CandidateProcessor AFTER initial processing
   *
   * @param candidate - Already processed candidate with potential shortfalls
   * @param initialResult - The result from initial processing
   * @param configuration - Configuration containing bond series
   */
  static adjust(
    candidate: Candidate,
    initialResult: ProcessingResult,
    configuration: Configuration
  ): TargetedProcessingResult {
    // This method is only called when we already know there are targeted bonds
    // The initial processing has already been done by CandidateProcessor

    const result = initialResult;

    logger.domain.compliance(
      `🎯 Finished core distribution for candidate ${candidate.name}. Now checking optimization.`
    );

    if (!result.success) {
      // If initial processing failed completely, no point in adjustments
      logger.domain.compliance(`❌ Initial processing failed. Skipping targeted optimization.`);
      return result;
    }

    // Note: We proceed even if not viable due to target compliance issues
    // because that's exactly what we're trying to fix!

    // Use the processed candidate from the result
    const processedCandidate = result.candidate;

    // Check DRF availability
    const drfAvailable = TargetedBondAdjuster.checkDRFAvailability(
      processedCandidate,
      configuration
    );
    if (!drfAvailable) {
      logger.domain.compliance(
        `⚠️ No DRF capacity available for optimization. All bonds will remain at current levels.`
      );
      return result;
    }

    // Step 2: Check for shortfalls and process if found
    const shortfalls = TargetedBondAdjuster.analyzeShortfalls(processedCandidate, configuration);

    if (shortfalls.length === 0) {
      logger.domain.compliance(`✅ All targeted bonds meet their targets. No optimization needed.`);
      return result;
    }

    logger.domain.compliance(
      `📊 Bond series shortfall detected and DRF balance available, processing optimization.`
    );

    // Step 3: Iterative adjustments for each targeted bond
    const targetedAdjustments: TargetedProcessingResult['targetedAdjustments'] = [];

    for (const shortfall of shortfalls) {
      logger.domain.compliance(`\n🔄 Processing Bond ${shortfall.bondSeriesId}:`);
      logger.domain.compliance(`   Current: $${shortfall.currentTotal.toLocaleString()}`);
      logger.domain.compliance(`   Target: $${shortfall.targetAmount.toLocaleString()}`);
      logger.domain.compliance(`   Shortfall: $${shortfall.shortfall.toLocaleString()}`);

      const adjustment = TargetedBondAdjuster.adjustForTargetedBond(
        processedCandidate,
        shortfall.bondSeriesId,
        shortfall.bondSeries,
        shortfall.targetAmount,
        configuration
      );

      if (adjustment) {
        targetedAdjustments.push(adjustment);
      }
    }

    // Track finalization result
    let finalizationResult: FinalizationResult | null = null;

    // If we made adjustments, we need to recalculate DRF and re-evaluate results
    if (targetedAdjustments.length > 0) {
      logger.domain.compliance(`📊 Summary of targeted adjustments:`);
      targetedAdjustments.forEach((adj) => {
        const status = adj.targetMet ? '✅ TARGET MET' : '⚠️ PARTIAL';
        logger.domain.compliance(
          `   ${adj.bondSeriesId}: Added $${adj.totalAdded.toLocaleString()} in ${adj.iterations} iteration(s) - ${status}`
        );
      });

      // Run final processing to update DRF balances and results
      logger.domain.compliance(`🔄 Running final processing after adjustments...`);

      // Finalize the candidate with all adjustments
      // We need to skip target compliance check since we just fixed it
      finalizationResult = ProcessingFinalizer.finalize(processedCandidate, {
        skipResultsEvaluation: false, // We DO want to recalculate DRF and viability
        skipTaxYearPlan: false, // We want tax year plan
        skipFiveYearRuleValidation: true, // Skip five-year rule for now
        phase: 'TARGETED-ADJUSTMENT',
        configuration: configuration, // Required for DRF calculations
      });

      if (finalizationResult.success) {
        // Mark the candidate as processed since we've completed all adjustments
        processedCandidate.isProcessed = true;

        // Remove target-related failure messages since we just fixed them
        if (processedCandidate.results.failureMessages) {
          processedCandidate.results.failureMessages =
            processedCandidate.results.failureMessages.filter(
              (msg) => !msg.includes('fails to reach target') && !msg.includes('exceeds target')
            );

          // If no failure messages remain, mark as viable
          if (processedCandidate.results.failureMessages.length === 0) {
            processedCandidate.results.isViable = true;
          }
        }

        logger.domain.compliance(
          `✅ Final processing complete for ${processedCandidate.name} - Viable: ${processedCandidate.results.isViable}`
        );

        // Log detailed results for debugging
        logger.domain.compliance(
          `   Total Principal: $${processedCandidate.results.totalPrincipal?.toLocaleString()}`
        );
        logger.domain.compliance(
          `   DRF Status: OK? ${!processedCandidate.results.failureMessages?.some((m) => m.includes('DRF'))}`
        );
        logger.domain.compliance(
          `   Target Compliance: OK? ${!processedCandidate.results.failureMessages?.some((m) => m.includes('target'))}`
        );
        if (
          processedCandidate.results.failureMessages &&
          processedCandidate.results.failureMessages.length > 0
        ) {
          logger.domain.compliance(
            `   Failure messages: ${processedCandidate.results.failureMessages.join(', ')}`
          );
        }
      } else {
        logger.domain.compliance(`⚠️ Final processing had issues: ${finalizationResult.error}`);
      }
    } else {
      logger.domain.compliance(
        `\n✅ Final processing complete for ${processedCandidate.name} (no adjustments needed)`
      );
    }

    // Return enhanced result with targeted adjustment information
    // If we made adjustments, we need to return success based on the final state
    const finalSuccess =
      targetedAdjustments.length > 0
        ? processedCandidate.isProcessed && finalizationResult?.success
        : result.success;

    return {
      success: finalSuccess ?? false,
      candidate: processedCandidate, // Return the updated candidate
      metrics: result.metrics,
      targetedAdjustments: targetedAdjustments.length > 0 ? targetedAdjustments : undefined,
    };
  }

  /**
   * Analyzes all targeted bonds to find shortfalls
   */
  private static analyzeShortfalls(
    candidate: Candidate,
    configuration: Configuration
  ): Array<{
    bondSeriesId: string;
    bondSeries: BondSeries;
    targetAmount: number;
    currentTotal: number;
    shortfall: number;
  }> {
    const shortfalls: Array<{
      bondSeriesId: string;
      bondSeries: BondSeries;
      targetAmount: number;
      currentTotal: number;
      shortfall: number;
    }> = [];

    // Sort bonds by issue order (earliest first)
    const targetedBonds = configuration.bondSeries
      .filter((bs) => bs.targetInformation?.targetType === TargetType.DOES_NOT_EXCEED)
      .sort((a, b) => {
        const aIndex = configuration.bondSeries.indexOf(a);
        const bIndex = configuration.bondSeries.indexOf(b);
        return aIndex - bIndex;
      });

    for (const bond of targetedBonds) {
      const currentTotal = TargetedBondAdjuster.calculateBondTotal(candidate, bond.id);
      const shortfall = calculateTargetShortfall(
        bond.targetInformation?.principalTargetAmount || 0,
        currentTotal
      );

      if (shortfall > TARGETED_BOND_PROCESSING.MIN_ADJUSTMENT) {
        shortfalls.push({
          bondSeriesId: bond.id,
          bondSeries: bond,
          targetAmount: bond.targetInformation?.principalTargetAmount || 0,
          currentTotal,
          shortfall,
        });
      }
    }

    return shortfalls;
  }

  /**
   * Checks if there's DRF capacity available for optimization
   */
  private static checkDRFAvailability(candidate: Candidate, configuration: Configuration): boolean {
    const { cashFlows } = candidate;
    const { drfInputs } = configuration;

    // Find minimum DRF balance across timeline
    let minDrf = Infinity;
    for (const cf of cashFlows) {
      minDrf = Math.min(minDrf, cf.drfEndingBalance);
    }

    // Check if we have capacity above minimum balance + safety buffer
    const minimumBalance = parseFloat(drfInputs.minimumBalance) || 0;
    const availableCapacity = calculateDrfAvailableCapacity(
      minDrf,
      minimumBalance,
      TARGETED_BOND_PROCESSING.DRF_SAFETY_BUFFER
    );
    return availableCapacity >= TARGETED_BOND_PROCESSING.MIN_ADJUSTMENT;
  }

  /**
   * Adjusts distribution for a single targeted bond
   * May iterate up to MAX_ITERATIONS_PER_BOND times
   */
  private static adjustForTargetedBond(
    candidate: Candidate,
    bondSeriesId: string,
    bondSeries: BondSeries,
    targetAmount: number,
    configuration: Configuration
  ): NonNullable<TargetedProcessingResult['targetedAdjustments']>[0] | null {
    let iterations = 0;
    let totalAdded = 0;
    let targetMet = false;

    for (let i = 0; i < TARGETED_BOND_PROCESSING.MAX_ITERATIONS_PER_BOND; i++) {
      // Calculate current shortfall
      const currentTotal = TargetedBondAdjuster.calculateBondTotal(candidate, bondSeriesId);
      const shortfall = calculateTargetShortfall(targetAmount, currentTotal);

      if (
        isTargetMetWithinTolerance(
          currentTotal,
          targetAmount,
          TARGETED_BOND_PROCESSING.MIN_ADJUSTMENT
        )
      ) {
        targetMet = true;
        logger.domain.compliance(`   ✅ Target met after ${iterations} iteration(s)`);
        break;
      }

      // Find capacity and determine adjustment
      const adjustment = TargetedBondAdjuster.findOptimalAdjustment(
        candidate,
        bondSeriesId,
        bondSeries,
        shortfall,
        configuration
      );

      if (!adjustment || adjustment.amount < TARGETED_BOND_PROCESSING.MIN_ADJUSTMENT) {
        logger.domain.compliance(`   ⚠️ No capacity available for further adjustments`);
        break;
      }

      // Log the adjustment we're about to make
      const cf = candidate.cashFlows[adjustment.periodIndex];
      logger.domain.compliance(
        `   💰 Adding $${adjustment.amount.toLocaleString()} to payment record ${cf.paymentDate} to satisfy bond targets`
      );

      // Apply adjustment
      TargetedBondAdjuster.applyAdjustment(candidate, bondSeriesId, adjustment);
      totalAdded += adjustment.amount;
      iterations++;

      // Recalculate all derived values using the principal recalculator
      PrincipalBasedRecalculationEngine.recalculateFromPrincipal(
        candidate,
        configuration.bondSeries
      );
    }

    if (iterations === 0) {
      return null;
    }

    // Final check if target was met
    const finalTotal = TargetedBondAdjuster.calculateBondTotal(candidate, bondSeriesId);
    targetMet = isTargetMetWithinTolerance(
      finalTotal,
      targetAmount,
      TARGETED_BOND_PROCESSING.MIN_ADJUSTMENT
    );

    return {
      bondSeriesId,
      iterations,
      totalAdded,
      targetMet,
    };
  }

  /**
   * Calculates total principal currently assigned to a bond
   */
  private static calculateBondTotal(candidate: Candidate, bondSeriesId: string): number {
    return candidate.cashFlows.reduce((sum, cf) => {
      const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bondSeriesId);
      return sum + (bondPayment?.principal || 0);
    }, 0);
  }

  /**
   * Finds the optimal adjustment for a bond with shortfall
   * Uses sophisticated capacity analysis considering interest cascade effects
   */
  private static findOptimalAdjustment(
    candidate: Candidate,
    bondSeriesId: string,
    bondSeries: BondSeries,
    shortfall: number,
    configuration: Configuration
  ): { periodIndex: number; amount: number } | null {
    const { cashFlows } = candidate;
    const { drfInputs } = configuration;

    // Calculate minimum DRF across entire timeline (forward-looking capacity)
    const minFutureDrf: number[] = new Array(cashFlows.length);
    let runningMin = Infinity;

    // Work backwards to find minimum DRF in all future periods
    for (let i = cashFlows.length - 1; i >= 0; i--) {
      runningMin = Math.min(runningMin, cashFlows[i].drfEndingBalance);
      minFutureDrf[i] = runningMin;
    }

    // Find the best period(s) to add principal
    const bestPeriods: Array<{ index: number; capacity: number; score: number }> = [];

    for (let i = 0; i < cashFlows.length; i++) {
      const cf = cashFlows[i];
      const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bondSeriesId);

      if (!bondPayment?.isActive || cf.isInterestOnly) {
        continue;
      }

      // Calculate DRF capacity at this point
      const minimumBalance = parseFloat(drfInputs.minimumBalance) || 0;
      const drfCapacity = calculateDrfAvailableCapacity(
        minFutureDrf[i],
        minimumBalance,
        TARGETED_BOND_PROCESSING.DRF_SAFETY_BUFFER
      );

      if (drfCapacity < TARGETED_BOND_PROCESSING.MIN_ADJUSTMENT) {
        continue;
      }

      // Calculate interest impact using compound factor
      const yearsFromCurrent = Math.floor(i / 2); // Assuming semi-annual payments
      const bondWithEscalation = bondSeries as BondSeries & {
        __interestRateEscalation?: { startRate: number };
      };
      const startRate = bondWithEscalation.__interestRateEscalation?.startRate || 0.05;
      const interestRate = bondPayment.interestRate || startRate;

      // For simplicity, use a conservative estimate of interest cascade
      // The actual cascade would be calculated by the recalculation engine
      const interestMultiplier = calculateInterestMultiplier(interestRate, yearsFromCurrent);

      // Maximum principal we can add considering interest effects
      const maxPrincipal = calculateMaxPrincipalWithInterest(drfCapacity, interestMultiplier);

      if (maxPrincipal >= TARGETED_BOND_PROCESSING.MIN_ADJUSTMENT) {
        // Score based on capacity and earliness (prefer earlier periods)
        const score = calculateOptimizationScore(maxPrincipal, i, cashFlows.length);
        bestPeriods.push({
          index: i,
          capacity: maxPrincipal,
          score,
        });
      }
    }

    if (bestPeriods.length === 0) {
      return null;
    }

    // Sort by score (best first)
    bestPeriods.sort((a, b) => b.score - a.score);

    // Use the best period
    const bestPeriod = bestPeriods[0];

    // Calculate amount to add (try to meet full shortfall if possible)
    const amountToAdd = calculateActualAdjustment(bestPeriod.capacity, shortfall);

    // Round to increment
    const roundedAmount =
      Math.floor(amountToAdd / TARGETED_BOND_PROCESSING.MIN_ADJUSTMENT) *
      TARGETED_BOND_PROCESSING.MIN_ADJUSTMENT;

    if (roundedAmount < TARGETED_BOND_PROCESSING.MIN_ADJUSTMENT) {
      return null;
    }

    return {
      periodIndex: bestPeriod.index,
      amount: roundedAmount,
    };
  }

  /**
   * Applies an adjustment to a specific bond payment
   */
  private static applyAdjustment(
    candidate: Candidate,
    bondSeriesId: string,
    adjustment: { periodIndex: number; amount: number }
  ): void {
    const cf = candidate.cashFlows[adjustment.periodIndex];
    const bondPayment = cf.bondPayments.find((bp) => bp.bondSeriesId === bondSeriesId);

    if (bondPayment) {
      bondPayment.principal += adjustment.amount;

      // Ensure final principal is rounded to PRINCIPAL_INCREMENT
      bondPayment.principal = roundPrincipalNearest(bondPayment.principal);
    }
  }
}
